import { useMemo } from "react";
import { SortableContext, useSortable } from "@dnd-kit/sortable";
import { useDndContext, type UniqueIdentifier } from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import { TaskCardComponent } from "./TaskCard";
import { Box, Flex, Heading, Button, Icon } from "@chakra-ui/react";
import { ScrollArea, ScrollBar } from "./ScrollArea";
import { MdDragHandle } from "react-icons/md";
import { TaskCard } from "models/tasks_type";
import { useLaunchedProject } from "../hooks/useLaunchedProject";

export interface Column {
  id: UniqueIdentifier;
  title: string;
}

export type ColumnType = "Column";

export interface ColumnDragData {
  type: ColumnType;
  column: Column;
}

type BoardColumnWithUseProps = Pick<useLaunchedProject.type, 'setShowTaskForm'>;

interface BoardColumnProps extends BoardColumnWithUseProps {
  column: Column;
  tasks: TaskCard[];
  isOverlay?: boolean;
}

export function BoardColumn({ column, tasks, isOverlay, setShowTaskForm }: BoardColumnProps) {
  const tasksIds = useMemo(() => tasks.map((task) => task.id), [tasks]);

  const {
    setNodeRef,
    attributes,
    listeners,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: column.id,
    data: {
      type: "Column",
      column,
    } satisfies ColumnDragData,
    attributes: {
      roleDescription: `Column: ${column.title}`,
    },
  });

  const style = {
    transition,
    transform: CSS.Translate.toString(transform),
  };

  const draggingStyle = isOverlay
    ? {
        boxShadow: "0 0 0 4px var(--chakra-colors-primary)",
        opacity: 0.7,
      }
    : isDragging
    ? {
        border: "2px solid var(--chakra-colors-primary)",
        opacity: 0.3,
      }
    : {};

  return (
    <Box
      ref={setNodeRef}
      style={{ ...style, ...draggingStyle }}
      w="350px"
      h="100vh"
      maxW="full"
      bg="gray.200"
      display="flex"
      flexDirection="column"
      flexShrink={0}
      border="none"
      borderRadius={"lg"}
    >
      <Flex
        px={4}
        py={2}
        fontWeight="semibold"
        align="center"
        justify="space-between"
        bg="gray.100"
        borderBottom="1px solid"
        borderColor="gray.300"
        borderTopRadius={"lg"}
      >
        <Heading as="span" size="md">
          {column.title}
        </Heading>
        <Button
          variant="ghost"
          {...attributes}
          {...listeners}
          p={1}
          color="primary.500"
          ml="auto"
          h="auto"
          cursor="grab"
        >
          <span style={{ visibility: "hidden" }}>{`Move column: ${column.title}`}</span>
          <Icon as={MdDragHandle} width='20px' height='20px' color='inherit' />
        </Button>
      </Flex>
      <ScrollArea>
        <Box display="flex" flexDirection="column" flexGrow={1} gap={2} p={2}>
          <SortableContext items={tasksIds}>
            {tasks.map((task) => (
              <TaskCardComponent key={task.id} task={task} setShowTaskForm={setShowTaskForm} />
            ))}
          </SortableContext>
        </Box>
      </ScrollArea>
    </Box>
  );
}

export function BoardContainer({ children }: { children: React.ReactNode }) {
  const dndContext = useDndContext();

  const isDragging = !!dndContext.active;

  return (
    <ScrollArea className={isDragging ? "snap-none" : "snap-x snap-mandatory"}>
      <Box
        display="flex"
        gap={4}
        alignItems="start"
        flexDirection="row"
        justifyContent="start"
        minH="full"
        px={2}
        pb={4}
      >
        {children}
      </Box>
      <ScrollBar orientation="horizontal" />
    </ScrollArea>
  );
}
